import { supabase, isSupabaseConfigured } from '../config/supabase';
import { queuedSupabaseOperation } from '../utils/supabaseWrapper';

// Global app settings table
const SETTINGS_TABLE = 'app_settings';

// Setting keys
export const SETTING_KEYS = {
  TMDB_API_KEY: 'tmdb_api_key',
  TMDB_BEARER_TOKEN: 'tmdb_bearer_token'
};

// Get a global setting by key
export const getGlobalSetting = async (key) => {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error('Supabase is not configured') };
  }
  
  try {
    const data = await queuedSupabaseOperation.select(
      SETTINGS_TABLE,
      `select=value&key=eq.${key}&is_active=eq.true`,
      null, // No localStorage fallback for settings
      `Get setting: ${key}`
    );

    const value = data && data.length > 0 ? data[0].value : null;
    return { data: value, error: null };
  } catch (err) {
    console.error(`Error getting global setting ${key}:`, err);
    return { data: null, error: err };
  }
};

// Set a global setting (admin only)
export const setGlobalSetting = async (key, value) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    // Get the current user to ensure proper authentication
    let user = null;
    
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        // Check if it's a network error
        if (userError.message?.includes('Failed to fetch') || userError.message?.includes('ERR_CONNECTION_CLOSED')) {
          throw new Error('Unable to connect to authentication server. Please check your internet connection and try again.');
        }
        throw userError;
      }
      user = userData.user;
    } catch (authError) {
      // If auth check fails, provide a more helpful error message
      if (authError.message?.includes('Failed to fetch') || authError.message?.includes('ERR_CONNECTION_CLOSED')) {
        throw new Error('Unable to connect to authentication server. Please check your internet connection and Supabase configuration.');
      }
      throw new Error(`Authentication check failed: ${authError.message}`);
    }
    
    if (!user) {
      throw new Error('User not authenticated. Please sign in and try again.');
    }

    // Use upsert to insert or update the setting
    const { data, error } = await supabase
      .from(SETTINGS_TABLE)
      .upsert([{
        key: key,
        value: value,
        is_active: true,
        updated_by: user.id,
        updated_at: new Date().toISOString()
      }], {
        onConflict: 'key'
      })
      .select()
      .single();

    if (error) {
      // Check if it's a network/connection error
      if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_CONNECTION_CLOSED')) {
        throw new Error('Unable to connect to database server. Please check your internet connection and try again.');
      }
      throw error;
    }
    
    return { data, error: null };
  } catch (err) {
    console.error(`Error setting global setting ${key}:`, err);
    return { data: null, error: err };
  }
};

// Delete a global setting (admin only)
export const deleteGlobalSetting = async (key) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from(SETTINGS_TABLE)
      .update({ 
        is_active: false,
        updated_by: user.id,
        updated_at: new Date().toISOString()
      })
      .eq('key', key)
      .select();

    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error(`Error deleting global setting ${key}:`, err);
    return { data: null, error: err };
  }
};

// Get all global settings (for admin use)
export const getAllGlobalSettings = async () => {
  if (!isSupabaseConfigured()) {
    return { data: [], error: new Error('Supabase is not configured') };
  }
  
  try {
    const { data, error } = await supabase
      .from(SETTINGS_TABLE)
      .select('*')
      .eq('is_active', true)
      .order('key');

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (err) {
    console.error('Error getting all global settings:', err);
    return { data: [], error: err };
  }
};

// Convenience functions for TMDB settings
export const getTMDBApiKey = async () => {
  return await getGlobalSetting(SETTING_KEYS.TMDB_API_KEY);
};

export const setTMDBApiKey = async (apiKey) => {
  return await setGlobalSetting(SETTING_KEYS.TMDB_API_KEY, apiKey);
};

export const getTMDBBearerToken = async () => {
  return await getGlobalSetting(SETTING_KEYS.TMDB_BEARER_TOKEN);
};

export const setTMDBBearerToken = async (bearerToken) => {
  return await setGlobalSetting(SETTING_KEYS.TMDB_BEARER_TOKEN, bearerToken);
};
